import { useState, useMemo, lazy, Suspense } from "react";
import IntranetLayout from "@/components/IntranetLayout";
const OCIMFReadinessCheck = lazy(() => import("@/components/OCIMFReadinessCheck"));
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  OVMSA_ELEMENTS, OVIQ_CHAPTERS,
  STATUS, STATUS_CONFIG,
  type StatusType
} from "@/data/ocimfData";
import {
  FileText, Download, Search, FolderOpen, ClipboardCheck, Eye, ExternalLink,
  CheckCircle2, MessageSquarePlus, ThumbsUp, ThumbsDown, FileBarChart, Clock,
  AlertCircle, User, Folder, ChevronRight, ArrowLeft, X, SlidersHorizontal,
  Calendar, Tag, Shield, BookOpen, History, Timer
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentReadingTime, formatReadingTime } from "@/hooks/useDocumentReadingTime";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";

const VIEWABLE_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "gif", "webp", "svg", "txt", "html", "htm"];
const OFFICE_VIEWER_EXTENSIONS = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];
const getFileExtension = (name: string) => {
  const parts = name.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
};

const getViewerEmbedUrl = (signedUrl: string, filePath: string) => {
  const extension = getFileExtension(filePath);

  if (OFFICE_VIEWER_EXTENSIONS.includes(extension)) {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(signedUrl)}`;
  }

  return signedUrl;
};

const VOTING_ROLES = ["executive_team", "dpa"];
const REPORT_USERS = ["jerome", "kyle", "bo"];

const DOCUMENT_FOLDERS = [
  { key: "Class and Flag", label: "Class and Flag", icon: "⚓", description: "Classification society & flag state rules and regulations" },
  { key: "Corporate Governance", label: "Corporate Governance", icon: "🏛️", description: "Corporate governance policies & frameworks" },
  { key: "DP Governance", label: "DP Governance", icon: "🛡️", description: "Dynamic Positioning governance & procedures" },
  { key: "General Ops", label: "General Ops", icon: "⚙️", description: "General operational documentation" },
  { key: "HR", label: "HR", icon: "👥", description: "Human resources policies & procedures" },
  { key: "Operational Excellence", label: "Operational Excellence", icon: "🏆", description: "Continuous improvement & best practices" },
  { key: "QMS", label: "QMS", icon: "📋", description: "Quality Management System documentation" },
  { key: "Security", label: "Security", icon: "🔐", description: "Restricted security documentation — clearance required", restricted: true },
  { key: "SMS", label: "SMS", icon: "🔒", description: "Safety Management System documentation" },
  { key: "Supply Chain", label: "Supply Chain", icon: "📦", description: "Procurement & logistics documentation" },
  { key: "Vessel Checklist", label: "Vessel Checklist", icon: "🚢", description: "Vessel inspection & operational checklists" },
];

const SMS_REVIEW_FOLDERS = [
  { key: "Corporate Governance", label: "Corporate Governance", icon: "🏛️", description: "Corporate governance documents under review" },
  { key: "DP Governance", label: "DP Governance", icon: "🛡️", description: "DP governance documents under review" },
  { key: "General Ops", label: "General Ops", icon: "⚙️", description: "General ops documents under review" },
  { key: "HR", label: "HR", icon: "👥", description: "HR documents under review" },
  { key: "Operational Excellence", label: "Operational Excellence", icon: "🏆", description: "Operational excellence documents under review" },
  { key: "QMS", label: "QMS", icon: "📋", description: "QMS documents under review" },
  { key: "Security", label: "Security", icon: "🔐", description: "Restricted security documents under review — clearance required", restricted: true },
  { key: "SMS", label: "SMS", icon: "🔒", description: "SMS documents under review" },
  { key: "Supply Chain", label: "Supply Chain", icon: "📦", description: "Supply chain documents under review" },
  { key: "Vessel Checklist", label: "Vessel Checklist", icon: "🚢", description: "Vessel checklist documents under review" },
];

const EmployeeDocuments = () => {
  const { user, employeePermissions, fullName } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [activeTab, setActiveTab] = useState<"operating" | "sms_reviews" | "client" | "ocimf">("operating");
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerEmbedUrl, setViewerEmbedUrl] = useState<string | null>(null);
  const [viewerName, setViewerName] = useState("");
  const [viewerDocId, setViewerDocId] = useState<string | null>(null);
  const [viewerSource, setViewerSource] = useState("documents");
  const [activeFolder, setActiveFolder] = useState<string | null>(null);
  const [smsActiveFolder, setSmsActiveFolder] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"name" | "date" | "category">("name");

  // Security clearance code state
  const [showSecurityAuth, setShowSecurityAuth] = useState(false);
  const [securityCodeInput, setSecurityCodeInput] = useState("");
  const [securityAuthLoading, setSecurityAuthLoading] = useState(false);
  const [securityCleared, setSecurityCleared] = useState(false);
  const [pendingSecurityDoc, setPendingSecurityDoc] = useState<any>(null);

  const hasSecurityAccess = !!employeePermissions?.page_security_docs;
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // SMS Review states
  const [showSuggestionForm, setShowSuggestionForm] = useState(false);
  const [selectedDocForSuggestion, setSelectedDocForSuggestion] = useState<any>(null);
  const [suggestionForm, setSuggestionForm] = useState({ section_reference: "", change_description: "", justification: "" });
  const [showVoteDialog, setShowVoteDialog] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<any>(null);
  const [voteComment, setVoteComment] = useState("");
  const [showReport, setShowReport] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);

  // SMS doc detail panel (click doc to see form + history)
  const [smsSelectedDoc, setSmsSelectedDoc] = useState<any>(null);

  const myRole = employeePermissions.employee_role || "crew";
  const canVote = VOTING_ROLES.includes(myRole);
  const canGenerateReport = REPORT_USERS.some(
    (n) => (fullName || "").toLowerCase().includes(n)
  ) || myRole === "executive_team" || myRole === "dpa";

  const resetViewer = () => {
    setViewerUrl(null);
    setViewerEmbedUrl(null);
    setViewerName("");
    setViewerDocId(null);
    setViewerSource("documents");
  };

  // Track reading time for currently viewed document
  useDocumentReadingTime(viewerDocId, viewerName, viewerSource, !!viewerUrl);

  // Load reading times for all docs
  const { data: readingTimes = [] } = useQuery({
    queryKey: ["doc-reading-times-emp", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data } = await supabase
        .from("document_reading_time" as any)
        .select("*")
        .eq("user_id", user.id);
      return (data as any) ?? [];
    },
    enabled: !!user,
    refetchOnWindowFocus: true,
  });

  const getReadingTime = (docId: string): number => {
    const record = readingTimes.find((r: any) => r.document_id === docId);
    return record ? (record as any).total_seconds : 0;
  };

  // Queries
  const { data: companies = [] } = useQuery({
    queryKey: ["emp-companies"],
    queryFn: async () => { const { data } = await supabase.from("companies").select("*").order("name"); return data ?? []; },
  });

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["emp-documents"],
    queryFn: async () => { const { data } = await supabase.from("documents").select("*, companies(name)").order("created_at", { ascending: false }); return data ?? []; },
  });

  const { data: operatingDocs = [], isLoading: opLoading } = useQuery({
    queryKey: ["emp-operating-documents"],
    queryFn: async () => {
      const { data } = await supabase.from("operating_documents").select("*").eq("is_archived", false).order("category").order("name");
      return data ?? [];
    },
  });

  const { data: suggestions = [] } = useQuery({
    queryKey: ["sms-suggestions"],
    queryFn: async () => {
      const { data } = await supabase.from("sms_change_suggestions").select("*").order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const { data: votes = [] } = useQuery({
    queryKey: ["sms-votes"],
    queryFn: async () => {
      const { data } = await supabase.from("sms_suggestion_votes").select("*");
      return data ?? [];
    },
  });

  const { data: readConfirmations = [] } = useQuery({
    queryKey: ["doc-read-confirmations"],
    queryFn: async () => {
      const { data } = await supabase.from("document_read_confirmations").select("*");
      return data ?? [];
    },
  });

  const { data: profiles = [] } = useQuery({
    queryKey: ["all-profiles-for-report"],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("user_id, full_name");
      return data ?? [];
    },
  });

  const {
    data: reportReadingTimes = [],
    isLoading: reportReadingTimesLoading,
    error: reportReadingTimesError,
  } = useQuery({
    queryKey: ["all-doc-reading-times-report"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_reading_time" as any)
        .select("*")
        .order("updated_at", { ascending: false });

      if (error) throw error;
      return (data as any) ?? [];
    },
    enabled: canGenerateReport,
    refetchInterval: canGenerateReport ? 5000 : false,
    refetchOnWindowFocus: true,
  });

  // OCIMF readiness items for report
  const { data: ocimfItems = [] } = useQuery({
    queryKey: ["ocimf-readiness-items-report"],
    queryFn: async () => {
      const { data } = await supabase.from("ocimf_readiness_items").select("*");
      return data ?? [];
    },
    enabled: canGenerateReport,
  });

  const getProfileName = (userId: string) => {
    const p = profiles.find((pr: any) => pr.user_id === userId);
    return p?.full_name || "Unknown User";
  };

  const getReportReadingTime = (docId: string, userId: string): number => {
    const record = reportReadingTimes.find((r: any) => r.document_id === docId && r.user_id === userId);
    return record ? (record as any).total_seconds : 0;
  };

  // Filtered docs
  const roleFilteredOpDocs = operatingDocs.filter((d: any) => {
    const reqRole = d.required_role;
    return !reqRole || reqRole === myRole;
  });

  const smsReviewDocs = roleFilteredOpDocs.filter((d: any) => {
    if (!d.is_sms_review) return false;
    // Security docs in SMS review require clearance
    const cat = (d.category || "").toLowerCase();
    if (cat === "security") {
      return hasSecurityAccess && securityCleared;
    }
    return true;
  });
  const nonSmsOpDocs = roleFilteredOpDocs; // Show all docs in Company Documents regardless of SMS flag

  const filtered = documents.filter((d) => {
    const matchSearch = d.name.toLowerCase().includes(search.toLowerCase()) || (d.description ?? "").toLowerCase().includes(search.toLowerCase());
    const matchCompany = !filterCompany || d.company_id === filterCompany;
    return matchSearch && matchCompany;
  });

  // Enhanced search for operating docs
  const searchFilteredOpDocs = useMemo(() => {
    const source = activeTab === "sms_reviews" ? smsReviewDocs : nonSmsOpDocs;
    if (!search.trim()) return source;
    const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
    return source.filter((d) => {
      const searchable = [d.name, d.description || "", d.category || "", d.version || "", d.display_section || ""].join(" ").toLowerCase();
      return terms.every((term) => searchable.includes(term));
    });
  }, [search, activeTab, smsReviewDocs, nonSmsOpDocs]);

  // Folder counts for operating tab
  const folderCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    DOCUMENT_FOLDERS.forEach((f) => { counts[f.key] = 0; });
    nonSmsOpDocs.forEach((d: any) => {
      const cat = d.category || "General Ops";
      if (counts[cat] !== undefined) counts[cat]++;
      else {
        const match = DOCUMENT_FOLDERS.find((f) => f.key.toLowerCase() === cat.toLowerCase());
        if (match) counts[match.key]++;
      }
    });
    return counts;
  }, [nonSmsOpDocs]);

  // SMS folder counts
  const smsFolderCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    SMS_REVIEW_FOLDERS.forEach((f) => { counts[f.key] = 0; });
    smsReviewDocs.forEach((d: any) => {
      const cat = d.category || "General Ops";
      if (counts[cat] !== undefined) counts[cat]++;
      else {
        const match = SMS_REVIEW_FOLDERS.find((f) => f.key.toLowerCase() === cat.toLowerCase());
        if (match) counts[match.key]++;
      }
    });
    return counts;
  }, [smsReviewDocs]);

  // Docs in the active folder (operating)
  const folderDocs = useMemo(() => {
    if (!activeFolder) return [];
    let docs = nonSmsOpDocs.filter((d: any) => (d.category || "General Ops").toLowerCase() === activeFolder.toLowerCase());
    if (search.trim()) {
      const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
      docs = docs.filter((d) => {
        const searchable = [d.name, d.description || "", d.version || ""].join(" ").toLowerCase();
        return terms.every((term) => searchable.includes(term));
      });
    }
    docs.sort((a, b) => {
      let cmp = 0;
      if (sortBy === "name") cmp = a.name.localeCompare(b.name);
      else if (sortBy === "date") cmp = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      return sortOrder === "desc" ? -cmp : cmp;
    });
    return docs;
  }, [activeFolder, nonSmsOpDocs, search, sortBy, sortOrder]);

  // SMS folder docs
  const smsFolderDocs = useMemo(() => {
    if (!smsActiveFolder) return [];
    let docs = smsReviewDocs.filter((d: any) => (d.category || "General Ops").toLowerCase() === smsActiveFolder.toLowerCase());
    if (search.trim()) {
      const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
      docs = docs.filter((d) => {
        const searchable = [d.name, d.description || "", d.version || ""].join(" ").toLowerCase();
        return terms.every((term) => searchable.includes(term));
      });
    }
    docs.sort((a, b) => {
      let cmp = 0;
      if (sortBy === "name") cmp = a.name.localeCompare(b.name);
      else if (sortBy === "date") cmp = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      return sortOrder === "desc" ? -cmp : cmp;
    });
    return docs;
  }, [smsActiveFolder, smsReviewDocs, search, sortBy, sortOrder]);

  // Global search results (across all folders)
  const globalSearchResults = useMemo(() => {
    if (!search.trim() || activeFolder) return [];
    return searchFilteredOpDocs;
  }, [search, activeFolder, searchFilteredOpDocs]);

  // SMS global search
  const smsGlobalSearchResults = useMemo(() => {
    if (!search.trim() || smsActiveFolder) return [];
    if (activeTab !== "sms_reviews") return [];
    const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
    return smsReviewDocs.filter((d) => {
      const searchable = [d.name, d.description || "", d.category || "", d.version || ""].join(" ").toLowerCase();
      return terms.every((term) => searchable.includes(term));
    });
  }, [search, smsActiveFolder, activeTab, smsReviewDocs]);

  // Helpers
  const hasRead = (docId: string) => readConfirmations.some((rc: any) => rc.document_id === docId && rc.user_id === user?.id);
  const getDocSuggestions = (docId: string) => suggestions.filter((s: any) => s.document_id === docId);
  const getSuggestionVotes = (suggestionId: string) => votes.filter((v: any) => v.suggestion_id === suggestionId);
  const hasVoted = (suggestionId: string) => votes.some((v: any) => v.suggestion_id === suggestionId && v.voter_id === user?.id);

  // Mutations
  const confirmRead = useMutation({
    mutationFn: async (docId: string) => {
      const { error } = await supabase.from("document_read_confirmations").insert({
        document_id: docId, user_id: user!.id, user_name: fullName || user!.email,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["doc-read-confirmations"] }); toast.success("Read confirmation recorded"); },
    onError: () => toast.error("Failed to record confirmation"),
  });

  const submitSuggestion = useMutation({
    mutationFn: async () => {
      if (!selectedDocForSuggestion) return;
      const { error } = await supabase.from("sms_change_suggestions").insert({
        document_id: selectedDocForSuggestion.id, suggested_by: user!.id,
        document_name: selectedDocForSuggestion.name,
        section_reference: suggestionForm.section_reference || null,
        change_description: suggestionForm.change_description,
        justification: suggestionForm.justification || null,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sms-suggestions"] });
      toast.success("Change suggestion submitted");
      setShowSuggestionForm(false);
      setSuggestionForm({ section_reference: "", change_description: "", justification: "" });
      // Keep smsSelectedDoc open to show updated history
      if (smsSelectedDoc) {
        // refresh will show the new suggestion
      }
    },
    onError: () => toast.error("Failed to submit suggestion"),
  });

  const submitVote = useMutation({
    mutationFn: async (vote: "approved" | "rejected") => {
      if (!selectedSuggestion) return;
      const { error } = await supabase.from("sms_suggestion_votes").insert({
        suggestion_id: selectedSuggestion.id, voter_id: user!.id,
        voter_name: fullName || user!.email, vote, comments: voteComment || null,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sms-votes"] });
      toast.success("Vote recorded");
      setShowVoteDialog(false);
      setVoteComment("");
      setSelectedSuggestion(null);
    },
    onError: () => toast.error("Failed to record vote"),
  });

  const handleDownload = async (filePath: string, fileName: string, bucket = "client-documents") => {
    const { data } = await supabase.storage.from(bucket).createSignedUrl(filePath, 60);
    if (data?.signedUrl) { const a = document.createElement("a"); a.href = data.signedUrl; a.download = fileName; a.click(); }
  };

  const handleView = async (
    filePath: string,
    fileName: string,
    bucket = "client-documents",
    docId?: string,
    source = "documents"
  ) => {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(filePath, 3600);

    if (error || !data?.signedUrl) {
      toast.error("Failed to open document viewer");
      return;
    }

    setViewerUrl(data.signedUrl);
    setViewerEmbedUrl(getViewerEmbedUrl(data.signedUrl, filePath));
    setViewerName(fileName);
    setViewerDocId(docId || null);
    setViewerSource(source);
  };

  // Security document access: intercept click, require re-auth
  const handleSecurityDocClick = (doc: any) => {
    if (!hasSecurityAccess) {
      toast.error("You do not have security clearance to access this document. Please contact your administrator.");
      return;
    }
    if (securityCleared) {
      handleView(doc.file_path, doc.name, "operating-documents", doc.id, "documents");
      return;
    }
    setPendingSecurityDoc(doc);
    setSecurityCodeInput("");
    setShowSecurityAuth(true);
  };

  const handleSecurityCodeVerify = async () => {
    if (!securityCodeInput.trim()) {
      toast.error("Please enter your security access code.");
      return;
    }
    setSecurityAuthLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("verify-security-code", {
        body: { code: securityCodeInput.trim() },
      });
      if (error) throw error;
      const result = data as { valid: boolean; reason?: string } | null;
      if (!result?.valid) {
        if (result?.reason === "expired") {
          toast.error("This security access code has expired. Please contact your administrator for a new code.");
        } else if (result?.reason === "no_code_assigned") {
          toast.error("No security access code is assigned. Please contact your administrator.");
        } else {
          toast.error("Invalid security access code. Please contact your administrator.");
        }
        return;
      }
      setSecurityCleared(true);
      setShowSecurityAuth(false);
      toast.success("Security clearance verified.");
      if (pendingSecurityDoc) {
        handleView(pendingSecurityDoc.file_path, pendingSecurityDoc.name, "operating-documents", pendingSecurityDoc.id, "documents");
        setPendingSecurityDoc(null);
      }
    } catch {
      toast.error("Verification failed.");
    } finally {
      setSecurityAuthLoading(false);
    }
  };


  const getStatusBadge = (status: string) => {

    switch (status) {
      case "approved": return <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">Approved</Badge>;
      case "rejected": return <Badge className="bg-destructive/10 text-destructive border-destructive/20">Rejected</Badge>;
      default: return <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">Pending Review</Badge>;
    }
  };

  // ===================== COMPLIANCE REPORT (PDF) =====================
  const generateReport = async () => {
    if (reportReadingTimesLoading) {
      toast.error("Reading-time data is still loading. Please try again in a moment.");
      return;
    }

    if (reportReadingTimesError) {
      toast.error("Unable to load employee reading-time data for the report.");
      return;
    }

    const jsPDFModule = await import("jspdf");
    const jsPDF = jsPDFModule.default;
    const autoTableModule = await import("jspdf-autotable");
    const autoTable = autoTableModule.default;

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentW = pageW - margin * 2;
    let y = 0;

    const now = new Date();
    const dateStr = now.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    const timeStr = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    const refCode = `SMR-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    // Brand colours (Velocity Offshore: Navy Deep #0F2848, Teal #3098D0, Lime #8FC040)
    const navy: [number, number, number] = [15, 40, 72];
    const teal: [number, number, number] = [48, 152, 208];
    const white: [number, number, number] = [255, 255, 255];
    const darkGray: [number, number, number] = [55, 65, 81];
    const lightGray: [number, number, number] = [243, 244, 246];

    const checkNewPage = (needed: number) => {
      if (y + needed > pageH - 25) {
        // Footer on current page
        doc.setFontSize(8);
        doc.setTextColor(...darkGray);
        doc.text(`Velocity Offshore — Confidential`, margin, pageH - 10);
        doc.text(`${refCode}`, pageW - margin, pageH - 10, { align: "right" });
        doc.addPage();
        y = 20;
      }
    };

    // ===== COVER PAGE =====
    // Navy header band
    doc.setFillColor(...navy);
    doc.rect(0, 0, pageW, 100, "F");

    // Teal accent line
    doc.setFillColor(...teal);
    doc.rect(0, 100, pageW, 3, "F");

    // Load logo and add to PDF
    try {
      const logoUrl = new URL("/src/assets/velocity-logo-light.png", window.location.origin).href;
      const logoResponse = await fetch(logoUrl);
      const logoBlob = await logoResponse.blob();
      const logoBase64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(logoBlob);
      });
      const logoW = 70;
      const logoH = 28;
      doc.addImage(logoBase64, "PNG", (pageW - logoW) / 2, 10, logoW, logoH);
    } catch {
      // Fallback: just text if logo fails
    }

    // Company name (below logo)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(28);
    doc.setTextColor(...white);
    doc.text("VELOCITY OFFSHORE", pageW / 2, 50, { align: "center" });

    // Tagline
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("Safe. Fast. Cost-efficient.", pageW / 2, 60, { align: "center" });

    // Teal accent underline
    doc.setDrawColor(...teal);
    doc.setLineWidth(0.5);
    doc.line(pageW / 2 - 30, 65, pageW / 2 + 30, 65);

    // Report title
    doc.setFontSize(14);
    doc.setTextColor(...white);
    doc.text("SMS DOCUMENT REVIEW", pageW / 2, 78, { align: "center" });
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("COMPLIANCE REPORT", pageW / 2, 90, { align: "center" });

    // Report metadata
    y = 120;
    doc.setFontSize(11);
    doc.setTextColor(...darkGray);
    doc.setFont("helvetica", "normal");

    const metaData = [
      ["Report Reference", refCode],
      ["Date Generated", `${dateStr} at ${timeStr}`],
      ["Generated By", fullName || user?.email || "System"],
      ["Classification", "CONFIDENTIAL — Internal Use Only"],
    ];

    metaData.forEach(([label, val]) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(val || "", margin + 50, y);
      y += 8;
    });

    // Description box
    y += 10;
    doc.setFillColor(...lightGray);
    doc.roundedRect(margin, y, contentW, 40, 3, 3, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    const descText = "This compliance report is produced in accordance with the International Safety Management (ISM) Code and ISO 9001:2015 Quality Management Systems standard. It provides a comprehensive audit of document review activities, read confirmations, and change management processes within the Velocity Offshore Safety Management System.";
    const descLines = doc.splitTextToSize(descText, contentW - 10);
    doc.text(descLines, margin + 5, y + 8);

    // ===== PAGE 2: REGULATORY FRAMEWORK =====
    doc.addPage();
    y = 20;

    // Section header helper
    const sectionHeader = (num: string, title: string) => {
      checkNewPage(20);
      doc.setFillColor(...navy);
      doc.rect(margin, y, contentW, 10, "F");
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...white);
      doc.text(`${num}. ${title}`, margin + 4, y + 7);
      y += 16;
    };

    const subHeader = (title: string) => {
      checkNewPage(12);
      doc.setFillColor(...teal);
      doc.rect(margin, y, 2, 8, "F");
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...navy);
      doc.text(title, margin + 6, y + 6);
      y += 12;
    };

    const bodyText = (text: string) => {
      doc.setFontSize(9.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...darkGray);
      const lines = doc.splitTextToSize(text, contentW - 5);
      checkNewPage(lines.length * 5);
      doc.text(lines, margin + 2, y);
      y += lines.length * 4.5 + 4;
    };

    const bulletPoint = (text: string) => {
      doc.setFontSize(9.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...darkGray);
      const lines = doc.splitTextToSize(text, contentW - 15);
      checkNewPage(lines.length * 5);
      doc.text("•", margin + 5, y);
      doc.text(lines, margin + 10, y);
      y += lines.length * 4.5 + 2;
    };

    sectionHeader("1", "REGULATORY & STANDARDS FRAMEWORK");

    subHeader("ISM Code (International Safety Management Code)");
    bodyText("This compliance report is produced in accordance with the following ISM Code chapters:");
    const ismItems = [
      "Chapter 1.4 — Functional requirements for a Safety Management System",
      "Chapter 5 — Master's Responsibility and Authority",
      "Chapter 6 — Resources and Personnel",
      "Chapter 7 — Shipboard Operations",
      "Chapter 9 — Reports and Analysis of Non-Conformities, Accidents",
      "Chapter 10 — Maintenance of the Ship and Equipment",
      "Chapter 11 — Documentation",
      "Chapter 12 — Company Verification, Review and Evaluation",
    ];
    ismItems.forEach(bulletPoint);
    y += 2;
    bodyText("The ISM Code requires that companies establish procedures to ensure documents are reviewed and updated as necessary, and that changes are communicated to all relevant personnel. This review process directly satisfies ISM Code Chapter 11 requirements for controlled documentation.");

    subHeader("ISO 9001:2015 (Quality Management Systems)");
    const isoItems = [
      "Clause 4.4 — Quality Management System and its Processes",
      "Clause 7.5 — Documented Information",
      "Clause 7.5.2 — Creating and Updating",
      "Clause 7.5.3 — Control of Documented Information",
      "Clause 8.1 — Operational Planning and Control",
      "Clause 9.1 — Monitoring, Measurement, Analysis and Evaluation",
      "Clause 10.2 — Nonconformity and Corrective Action",
      "Clause 10.3 — Continual Improvement",
    ];
    isoItems.forEach(bulletPoint);
    y += 2;
    bodyText("ISO 9001:2015 mandates that organisations control documented information to ensure it remains suitable, adequate, and effective. The document review process, including stakeholder feedback and approval workflows, satisfies the requirements for continual improvement (PDCA cycle).");

    // ===== SCOPE & METHODOLOGY =====
    sectionHeader("2", "SCOPE & METHODOLOGY");
    bodyText("This report covers all operating documents flagged for SMS Review within the Velocity Offshore Document Management System. The review process comprises:");
    y += 2;
    bulletPoint("Document Distribution & Read Confirmation (ISM 11, ISO 7.5.3) — Personnel are required to acknowledge receipt and review of each document, creating an auditable trail of awareness.");
    bulletPoint("Change Suggestion Submission (ISM 12, ISO 10.3) — Any authorised user may propose amendments to controlled documents, referencing specific sections and providing justification.");
    bulletPoint("Executive Review & Voting (ISM 12.1, ISO 9.1) — The Executive Team and DPA review proposed changes and cast approval or rejection votes with recorded rationale.");

    // ===== DOCUMENT READ CONFIRMATIONS (Matrix Table) =====
    sectionHeader("3", "DOCUMENT READ CONFIRMATIONS");

    const smsDocIds = new Set(smsReviewDocs.map((d: any) => d.id));
    const totalDocs = smsReviewDocs.length;
    const smsReadConfirmations = readConfirmations.filter((rc: any) => smsReviewDocs.some((d: any) => d.id === rc.document_id));
    const smsReadingTimes = reportReadingTimes.filter((rt: any) => smsDocIds.has(rt.document_id));
    const totalReads = smsReadConfirmations.length;
    const totalReadingSeconds = smsReadingTimes.reduce((sum: number, rt: any) => sum + (rt.total_seconds || 0), 0);

    // Collect unique employee names from all read confirmations
    const employeeMap = new Map<string, string>();
    smsReadConfirmations.forEach((rc: any) => {
      if (!employeeMap.has(rc.user_id)) {
        employeeMap.set(rc.user_id, rc.user_name || getProfileName(rc.user_id));
      }
    });
    smsReadingTimes.forEach((rt: any) => {
      if (!employeeMap.has(rt.user_id)) {
        employeeMap.set(rt.user_id, getProfileName(rt.user_id));
      }
    });
    // Also add known report users from profiles
    profiles.forEach((p: any) => {
      if (!employeeMap.has(p.user_id) && p.full_name) {
        const nameLower = p.full_name.toLowerCase();
        if (REPORT_USERS.some(n => nameLower.includes(n))) {
          employeeMap.set(p.user_id, p.full_name);
        }
      }
    });
    const employeeList = Array.from(employeeMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
    const employeeNames = employeeList.map(([, name]) => name);
    const employeeIds = employeeList.map(([id]) => id);

    // Summary stats
    doc.setFillColor(...lightGray);
    doc.roundedRect(margin, y, contentW, 24, 2, 2, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...navy);
    doc.text(`Total SMS Review Documents: ${totalDocs}`, margin + 5, y + 7);
    doc.text(`Total Read Confirmations: ${totalReads}  |  Personnel Tracked: ${employeeNames.length}`, margin + 5, y + 14);
    doc.text(`Cumulative Reading Time: ${formatReadingTime(totalReadingSeconds)}`, margin + 5, y + 21);
    y += 30;

    bodyText("Matrix cells show each employee's confirmation status and cumulative reading time for the document.");

    // Matrix table: documents as rows, employees as columns
    const docsByCategory: Record<string, any[]> = {};
    smsReviewDocs.forEach((d: any) => {
      const cat = d.category || "General";
      if (!docsByCategory[cat]) docsByCategory[cat] = [];
      docsByCategory[cat].push(d);
    });

    // Calculate column widths dynamically
    const docColWidth = Math.min(65, contentW * 0.35);
    const remainingWidth = contentW - docColWidth;
    const empColWidth = employeeNames.length > 0 ? Math.min(remainingWidth / employeeNames.length, 30) : 20;

    Object.entries(docsByCategory).sort(([a], [b]) => a.localeCompare(b)).forEach(([category, docs]) => {
      checkNewPage(20);
      subHeader(category);

      const tableHead = ["Document", ...employeeNames.map(n => {
        // Use first name or abbreviate
        const parts = n.split(" ");
        return parts[0];
      })];

      const tableData = docs.map((d: any) => {
        const shortName = d.name.split("/").pop() || d.name;
        const cells = [shortName];
        employeeIds.forEach((empId) => {
          const rc = readConfirmations.find((r: any) => r.document_id === d.id && r.user_id === empId);
          const readingSeconds = getReportReadingTime(d.id, empId);

          if (rc && readingSeconds > 0) {
            cells.push(`✓ ${formatReadingTime(readingSeconds)}`);
          } else if (readingSeconds > 0) {
            cells.push(formatReadingTime(readingSeconds));
          } else if (rc) {
            cells.push("✓ Confirmed");
          } else {
            cells.push("—");
          }
        });
        return cells;
      });

      // Build column styles
      const colStyles: Record<number, any> = { 0: { cellWidth: docColWidth } };
      employeeNames.forEach((_, i) => {
        colStyles[i + 1] = { cellWidth: empColWidth, halign: "center" as const };
      });

      autoTable(doc, {
        startY: y,
        head: [tableHead],
        body: tableData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 6.5, cellPadding: 1.5, textColor: darkGray, lineColor: [220, 220, 220] as any, lineWidth: 0.2, overflow: "linebreak" as const },
        headStyles: { fillColor: navy, textColor: white, fontStyle: "bold", fontSize: 7 },
        alternateRowStyles: { fillColor: [249, 250, 251] as any },
        columnStyles: colStyles,
        tableWidth: "auto" as const,
        didParseCell: (data: any) => {
          if (data.section === "body" && data.column.index > 0) {
            const val = data.cell.raw as string;
            if (val.startsWith("✓")) {
              data.cell.styles.textColor = [16, 185, 129]; // emerald
              data.cell.styles.fontStyle = "bold";
            } else {
              data.cell.styles.textColor = [180, 180, 180];
            }
          }
        },
        didDrawPage: () => {
          doc.setFontSize(8);
          doc.setTextColor(...darkGray);
          doc.text(`Velocity Offshore — Confidential`, margin, pageH - 10);
          doc.text(`${refCode}`, pageW - margin, pageH - 10, { align: "right" });
        },
      });

      y = (doc as any).lastAutoTable.finalY + 8;
    });

    // Per-employee completion summary
    checkNewPage(20);
    subHeader("Personnel Completion Summary");
    const summaryData = employeeList.map(([empId, empName]) => {
      const readCount = smsReadConfirmations.filter((rc: any) => rc.user_id === empId).length;
      const readingSeconds = smsReadingTimes
        .filter((rt: any) => rt.user_id === empId)
        .reduce((sum: number, rt: any) => sum + (rt.total_seconds || 0), 0);
      const pct = totalDocs > 0 ? Math.round((readCount / totalDocs) * 100) : 0;
      return [empName, String(readCount), String(totalDocs), formatReadingTime(readingSeconds), `${pct}%`];
    });

    autoTable(doc, {
      startY: y,
      head: [["Employee", "Read", "Total Docs", "Reading Time", "Completion"]],
      body: summaryData,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2.5, textColor: darkGray, lineColor: [220, 220, 220] as any, lineWidth: 0.2 },
      headStyles: { fillColor: navy, textColor: white, fontStyle: "bold", fontSize: 8.5 },
      alternateRowStyles: { fillColor: [249, 250, 251] as any },
      columnStyles: {
        3: { halign: "center" as const, fontStyle: "bold" as const },
        4: { halign: "center" as const, fontStyle: "bold" as const },
      },
      didDrawPage: () => {
        doc.setFontSize(8);
        doc.setTextColor(...darkGray);
        doc.text(`Velocity Offshore — Confidential`, margin, pageH - 10);
        doc.text(`${refCode}`, pageW - margin, pageH - 10, { align: "right" });
      },
    });
    y = (doc as any).lastAutoTable.finalY + 8;

    // ===== CHANGE SUGGESTIONS =====
    checkNewPage(30);
    sectionHeader("4", "CHANGE SUGGESTIONS & EXECUTIVE REVIEW");

    const totalSuggestions = suggestions.length;
    const pendingSuggestions = suggestions.filter((s: any) => s.status === "pending").length;
    const approvedSuggestions = suggestions.filter((s: any) => s.status === "approved").length;
    const rejectedSuggestions = suggestions.filter((s: any) => s.status === "rejected").length;

    // Stats boxes
    doc.setFillColor(...lightGray);
    doc.roundedRect(margin, y, contentW, 22, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...navy);
    doc.text(`Total Change Suggestions: ${totalSuggestions}`, margin + 5, y + 7);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...darkGray);
    doc.text(`Pending: ${pendingSuggestions}   |   Approved: ${approvedSuggestions}   |   Rejected: ${rejectedSuggestions}`, margin + 5, y + 15);
    y += 28;

    if (suggestions.length > 0) {
      const suggestionData = suggestions.map((s: any, idx: number) => {
        const sVotes = getSuggestionVotes(s.id);
        const approvedV = sVotes.filter((v: any) => v.vote === "approved").length;
        const rejectedV = sVotes.filter((v: any) => v.vote === "rejected").length;
        const submitterName = getProfileName(s.suggested_by);
        return [
          String(idx + 1),
          s.document_name?.split("/").pop() || s.document_name,
          s.section_reference || "General",
          submitterName,
          s.status.toUpperCase(),
          `${approvedV}/${rejectedV}`,
          new Date(s.created_at).toLocaleDateString("en-GB"),
        ];
      });

      autoTable(doc, {
        startY: y,
        head: [["#", "Document", "Section", "Submitted By", "Status", "Approve/Reject", "Date"]],
        body: suggestionData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 7.5, cellPadding: 2, textColor: darkGray, lineColor: [220, 220, 220] as any, lineWidth: 0.2 },
        headStyles: { fillColor: navy, textColor: white, fontStyle: "bold", fontSize: 8 },
        alternateRowStyles: { fillColor: [249, 250, 251] as any },
        columnStyles: { 0: { cellWidth: 8, halign: "center" as const }, 4: { fontStyle: "bold" as const } },
        didDrawPage: () => {
          doc.setFontSize(8);
          doc.setTextColor(...darkGray);
          doc.text(`Velocity Offshore — Confidential`, margin, pageH - 10);
          doc.text(`${refCode}`, pageW - margin, pageH - 10, { align: "right" });
        },
      });

      y = (doc as any).lastAutoTable.finalY + 8;

      // Detail for each suggestion
      suggestions.forEach((s: any, idx: number) => {
        checkNewPage(40);
        const sVotes = getSuggestionVotes(s.id);
        const submitterName = getProfileName(s.suggested_by);

        doc.setFillColor(...lightGray);
        doc.roundedRect(margin, y, contentW, 6, 1, 1, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(...navy);
        doc.text(`Suggestion #${idx + 1}: ${s.document_name?.split("/").pop() || s.document_name}`, margin + 3, y + 4.5);
        y += 10;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(...darkGray);
        doc.text(`Submitted by: ${submitterName}  |  Date: ${new Date(s.created_at).toLocaleString("en-GB")}  |  Status: ${s.status.toUpperCase()}`, margin + 2, y);
        y += 6;

        doc.setFont("helvetica", "bold");
        doc.text("Proposed Change:", margin + 2, y);
        y += 4;
        doc.setFont("helvetica", "normal");
        const changeLines = doc.splitTextToSize(s.change_description, contentW - 10);
        checkNewPage(changeLines.length * 4);
        doc.text(changeLines, margin + 4, y);
        y += changeLines.length * 3.8 + 4;

        if (s.justification) {
          doc.setFont("helvetica", "bold");
          doc.text("Justification:", margin + 2, y);
          y += 4;
          doc.setFont("helvetica", "normal");
          const justLines = doc.splitTextToSize(s.justification, contentW - 10);
          checkNewPage(justLines.length * 4);
          doc.text(justLines, margin + 4, y);
          y += justLines.length * 3.8 + 4;
        }

        if (sVotes.length > 0) {
          doc.setFont("helvetica", "bold");
          doc.text("Voting Record:", margin + 2, y);
          y += 5;
          sVotes.forEach((v: any) => {
            checkNewPage(8);
            const icon = v.vote === "approved" ? "✓ APPROVED" : "✗ REJECTED";
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.text(`${icon} — ${v.voter_name || "Unknown"} (${new Date(v.created_at).toLocaleDateString("en-GB")})${v.comments ? `: "${v.comments}"` : ""}`, margin + 4, y);
            y += 5;
          });
        }
        y += 6;
      });
    }

    // ===== COMPLIANCE SUMMARY =====
    checkNewPage(60);
    sectionHeader("5", "COMPLIANCE SUMMARY & OBSERVATIONS");

    subHeader("ISM Code Compliance");
    bulletPoint(`Ch.11 Documentation Control — ${totalDocs > 0 ? "ACTIVE" : "NO DOCUMENTS"}: ${totalDocs} documents under controlled review`);
    bulletPoint(`Ch.12 Verification & Review — ${totalSuggestions > 0 ? "ACTIVE" : "NO ACTIVITY"}: ${totalSuggestions} change proposals processed`);
    bulletPoint(`Read Confirmations — ${totalReads} acknowledgements recorded across ${totalDocs} documents with ${formatReadingTime(totalReadingSeconds)} cumulative reading time logged`);
    y += 2;

    subHeader("ISO 9001:2015 Compliance");
    bulletPoint("Cl.7.5 Documented Information — Controlled document distribution active");
    bulletPoint("Cl.9.1 Monitoring & Measurement — Document awareness tracking in place");
    bulletPoint(`Cl.10.3 Continual Improvement — ${totalSuggestions} improvement proposals submitted`);
    y += 4;

    if (pendingSuggestions > 0) {
      checkNewPage(15);
      doc.setFillColor(255, 243, 205);
      doc.roundedRect(margin, y, contentW, 14, 2, 2, "F");
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(146, 64, 14);
      doc.text(`⚠ OBSERVATION: ${pendingSuggestions} change suggestion(s) remain pending executive review.`, margin + 4, y + 6);
      doc.setFont("helvetica", "normal");
      doc.text("ACTION: Executive Team / DPA to complete review and voting.", margin + 4, y + 11);
      y += 20;
    }

    const docsWithNoReads = smsReviewDocs.filter((d: any) => !readConfirmations.some((rc: any) => rc.document_id === d.id));
    if (docsWithNoReads.length > 0) {
      checkNewPage(15);
      doc.setFillColor(255, 228, 230);
      doc.roundedRect(margin, y, contentW, 14, 2, 2, "F");
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(153, 27, 27);
      doc.text(`⚠ ${docsWithNoReads.length} document(s) have no read confirmations recorded.`, margin + 4, y + 6);
      doc.setFont("helvetica", "normal");
      doc.text("ACTION: Ensure all relevant personnel review and confirm.", margin + 4, y + 11);
      y += 20;
    }

    // ===== OCIMF READINESS OVERVIEW =====
    checkNewPage(40);
    sectionHeader("6", "OCIMF READINESS OVERVIEW");

    // Build OCIMF status maps from DB items
    const ocimfKpiStatus: Record<string, string> = {};
    const ocimfItemStatus: Record<string, string> = {};
    const ocimfNotes: Record<string, string> = {};
    ocimfItems.forEach((i: any) => {
      if (i.section === "ovmsa") ocimfKpiStatus[i.item_id] = i.status;
      else ocimfItemStatus[i.item_id] = i.status;
      if (i.note) ocimfNotes[i.item_id] = i.note;
    });

    // Calculate scores
    let ovmsaTotal = 0, ovmsaDone = 0;
    OVMSA_ELEMENTS.forEach(e => e.subelements.forEach(s => s.kpis.forEach(k => {
      ovmsaTotal++;
      if (ocimfKpiStatus[k.id] === "complete") ovmsaDone++;
    })));
    const ovmsaPct = ovmsaTotal ? Math.round((ovmsaDone / ovmsaTotal) * 100) : 0;

    let oviqTotal = 0, oviqDone = 0;
    OVIQ_CHAPTERS.forEach(c => c.items.forEach(item => {
      oviqTotal++;
      if (ocimfItemStatus[item.id] === "complete") oviqDone++;
    }));
    const oviqPct = oviqTotal ? Math.round((oviqDone / oviqTotal) * 100) : 0;
    const overallPct = Math.round((ovmsaPct + oviqPct) / 2);

    // Summary box
    doc.setFillColor(...lightGray);
    doc.roundedRect(margin, y, contentW, 28, 2, 2, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...navy);
    doc.text(`Overall OCIMF Readiness: ${overallPct}%`, margin + 5, y + 7);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...darkGray);
    doc.text(`OVMSA2 Score: ${ovmsaPct}% (${ovmsaDone}/${ovmsaTotal} complete)  |  OVIQ4 Score: ${oviqPct}% (${oviqDone}/${oviqTotal} complete)`, margin + 5, y + 15);
    const readinessLabel = overallPct >= 80 ? "READY" : overallPct >= 50 ? "IN PROGRESS" : "ACTION REQUIRED";
    doc.text(`Status: ${readinessLabel}`, margin + 5, y + 23);
    y += 34;

    // OVMSA Element breakdown table
    subHeader("OVMSA2 Element Scores");
    const ovmsaTableData = OVMSA_ELEMENTS.map(el => {
      let elTotal = 0, elDone = 0, elPartial = 0;
      el.subelements.forEach(s => s.kpis.forEach(k => {
        elTotal++;
        if (ocimfKpiStatus[k.id] === "complete") elDone++;
        else if (ocimfKpiStatus[k.id] === "partial") elPartial++;
      }));
      const elPct = elTotal ? Math.round((elDone / elTotal) * 100) : 0;
      return [`E${el.code}`, el.title, String(elDone), String(elPartial), String(elTotal - elDone - elPartial), `${elPct}%`];
    });

    autoTable(doc, {
      startY: y,
      head: [["Element", "Title", "Complete", "In Progress", "Not Started", "Score"]],
      body: ovmsaTableData,
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: 2, textColor: darkGray, lineColor: [220, 220, 220] as any, lineWidth: 0.2 },
      headStyles: { fillColor: navy, textColor: white, fontStyle: "bold", fontSize: 8 },
      alternateRowStyles: { fillColor: [249, 250, 251] as any },
      columnStyles: { 0: { cellWidth: 15 }, 5: { halign: "center" as const, fontStyle: "bold" as const } },
      didDrawPage: () => {
        doc.setFontSize(8);
        doc.setTextColor(...darkGray);
        doc.text(`Velocity Offshore — Confidential`, margin, pageH - 10);
        doc.text(`${refCode}`, pageW - margin, pageH - 10, { align: "right" });
      },
    });
    y = (doc as any).lastAutoTable.finalY + 8;

    // OVIQ Chapter breakdown table
    checkNewPage(20);
    subHeader("OVIQ4 Chapter Scores");
    const oviqTableData = OVIQ_CHAPTERS.map(ch => {
      const chTotal = ch.items.length;
      const chDone = ch.items.filter(i => ocimfItemStatus[i.id] === "complete").length;
      const chPartial = ch.items.filter(i => ocimfItemStatus[i.id] === "partial").length;
      const chPct = chTotal ? Math.round((chDone / chTotal) * 100) : 0;
      return [ch.code, ch.title, String(chDone), String(chPartial), String(chTotal - chDone - chPartial), `${chPct}%`];
    });

    autoTable(doc, {
      startY: y,
      head: [["Chapter", "Title", "Complete", "In Progress", "Not Started", "Score"]],
      body: oviqTableData,
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: 2, textColor: darkGray, lineColor: [220, 220, 220] as any, lineWidth: 0.2 },
      headStyles: { fillColor: navy, textColor: white, fontStyle: "bold", fontSize: 8 },
      alternateRowStyles: { fillColor: [249, 250, 251] as any },
      columnStyles: { 0: { cellWidth: 15 }, 5: { halign: "center" as const, fontStyle: "bold" as const } },
      didDrawPage: () => {
        doc.setFontSize(8);
        doc.setTextColor(...darkGray);
        doc.text(`Velocity Offshore — Confidential`, margin, pageH - 10);
        doc.text(`${refCode}`, pageW - margin, pageH - 10, { align: "right" });
      },
    });
    y = (doc as any).lastAutoTable.finalY + 8;

    // ===== DOCUMENT AUTHENTICITY =====
    checkNewPage(40);
    sectionHeader("7", "DOCUMENT AUTHENTICITY");
    bodyText("This report has been auto-generated by the Velocity Offshore Document Management System. All timestamps are recorded in UTC and verified against the system audit log. Data integrity is maintained through authenticated user sessions with role-based access control.");
    y += 2;
    bodyText("This document satisfies the record-keeping requirements of:");
    bulletPoint("ISM Code Chapter 11 (Documentation)");
    bulletPoint("ISM Code Chapter 12 (Company Verification, Review and Evaluation)");
    bulletPoint("ISO 9001:2015 Clause 7.5 (Documented Information)");
    bulletPoint("ISO 9001:2015 Clause 9.1 (Monitoring, Measurement, Analysis)");
    bulletPoint("OCIMF OVMSA2 (2nd Edition, 2019) — Operator Vessel Management Self Assessment");
    bulletPoint("OCIMF OVIQ4 v7300 (April 2025) — Offshore Vessel Inspection Questionnaire");

    // Final footer band
    y += 10;
    checkNewPage(20);
    doc.setFillColor(...navy);
    doc.rect(margin, y, contentW, 12, "F");
    doc.setFillColor(...teal);
    doc.rect(margin, y, contentW, 2, "F");
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...white);
    doc.text("— END OF REPORT —", pageW / 2, y + 8, { align: "center" });

    // Page numbers
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(...darkGray);
      doc.text(`Page ${i} of ${totalPages}`, pageW / 2, pageH - 10, { align: "center" });
    }

    doc.save(`SMS-Review-Compliance-Report-${now.toISOString().split("T")[0]}.pdf`);
    toast.success("Compliance report PDF downloaded");
  };

  // ===================== COMPANY DOCUMENT VIEW REPORT =====================
  const generateDocumentViewReport = async () => {
    if (reportReadingTimesLoading) {
      toast.error("Reading-time data is still loading. Please try again in a moment.");
      return;
    }

    const jsPDFModule = await import("jspdf");
    const jsPDF = jsPDFModule.default;
    const autoTableModule = await import("jspdf-autotable");
    const autoTable = autoTableModule.default;

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentW = pageW - margin * 2;
    let y = 0;

    const now = new Date();
    const dateStr = now.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    const timeStr = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    const refCode = `DVR-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    const navy: [number, number, number] = [15, 40, 72];
    const teal: [number, number, number] = [48, 152, 208];
    const white: [number, number, number] = [255, 255, 255];
    const darkGray: [number, number, number] = [55, 65, 81];
    const lightGray: [number, number, number] = [243, 244, 246];

    const pageFooter = () => {
      doc.setFontSize(8);
      doc.setTextColor(...darkGray);
      doc.text(`Velocity Offshore — Confidential`, margin, pageH - 8);
      doc.text(refCode, pageW - margin, pageH - 8, { align: "right" });
    };

    const checkNewPage = (needed: number) => {
      if (y + needed > pageH - 20) {
        pageFooter();
        doc.addPage();
        y = 15;
      }
    };

    // Cover
    doc.setFillColor(...navy);
    doc.rect(0, 0, pageW, 70, "F");
    doc.setFillColor(...teal);
    doc.rect(0, 70, pageW, 2, "F");

    try {
      const logoUrl = new URL("/src/assets/velocity-logo-light.png", window.location.origin).href;
      const logoResponse = await fetch(logoUrl);
      const logoBlob = await logoResponse.blob();
      const logoBase64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(logoBlob);
      });
      doc.addImage(logoBase64, "PNG", (pageW - 60) / 2, 8, 60, 24);
    } catch {}

    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(...white);
    doc.text("COMPANY DOCUMENT VIEW REPORT", pageW / 2, 48, { align: "center" });
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("Who opened each document & viewing duration", pageW / 2, 58, { align: "center" });

    y = 82;
    doc.setFontSize(10);
    doc.setTextColor(...darkGray);
    doc.setFont("helvetica", "normal");
    const metaPairs = [
      ["Report Reference", refCode],
      ["Date Generated", `${dateStr} at ${timeStr}`],
      ["Generated By", fullName || user?.email || "System"],
      ["Classification", "CONFIDENTIAL — Internal Use Only"],
    ];
    metaPairs.forEach(([label, val]) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(val || "", margin + 45, y);
      y += 7;
    });

    // Collect all employees who have reading time data
    const employeeMap = new Map<string, string>();
    reportReadingTimes.forEach((rt: any) => {
      if (!employeeMap.has(rt.user_id)) {
        employeeMap.set(rt.user_id, getProfileName(rt.user_id));
      }
    });
    profiles.forEach((p: any) => {
      if (!employeeMap.has(p.user_id) && p.full_name) {
        employeeMap.set(p.user_id, p.full_name);
      }
    });
    const empList = Array.from(employeeMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
    const empNames = empList.map(([, n]) => n);
    const empIds = empList.map(([id]) => id);

    // Group operating docs by category
    const docsByCategory: Record<string, any[]> = {};
    operatingDocs.forEach((d: any) => {
      const cat = d.category || "General";
      if (!docsByCategory[cat]) docsByCategory[cat] = [];
      docsByCategory[cat].push(d);
    });

    doc.addPage();
    y = 15;

    const docColWidth = Math.min(70, contentW * 0.3);
    const remainingWidth = contentW - docColWidth;
    const empColWidth = empNames.length > 0 ? Math.min(remainingWidth / empNames.length, 28) : 20;

    Object.entries(docsByCategory).sort(([a], [b]) => a.localeCompare(b)).forEach(([category, docs]) => {
      checkNewPage(25);

      // Category header
      doc.setFillColor(...teal);
      doc.rect(margin, y, 2, 7, "F");
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...navy);
      doc.text(category, margin + 5, y + 5);
      y += 10;

      const tableHead = ["Document", ...empNames.map(n => n.split(" ")[0])];
      const tableData = docs.map((d: any) => {
        const shortName = d.name?.split("/").pop() || d.name;
        const cells = [shortName];
        empIds.forEach((empId) => {
          const readingSeconds = getReportReadingTime(d.id, empId);
          if (readingSeconds > 0) {
            cells.push(formatReadingTime(readingSeconds));
          } else {
            cells.push("—");
          }
        });
        return cells;
      });

      const colStyles: Record<number, any> = { 0: { cellWidth: docColWidth } };
      empNames.forEach((_, i) => { colStyles[i + 1] = { cellWidth: empColWidth, halign: "center" as const }; });

      autoTable(doc, {
        startY: y,
        head: [tableHead],
        body: tableData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 6, cellPadding: 1.5, textColor: darkGray, lineColor: [220, 220, 220] as any, lineWidth: 0.2, overflow: "linebreak" as const },
        headStyles: { fillColor: navy, textColor: white, fontStyle: "bold", fontSize: 6.5 },
        alternateRowStyles: { fillColor: [249, 250, 251] as any },
        columnStyles: colStyles,
        tableWidth: "auto" as const,
        didParseCell: (data: any) => {
          if (data.section === "body" && data.column.index > 0) {
            const val = data.cell.raw as string;
            if (val !== "—") {
              data.cell.styles.textColor = [0, 130, 140];
              data.cell.styles.fontStyle = "bold";
            } else {
              data.cell.styles.textColor = [180, 180, 180];
            }
          }
        },
        didDrawPage: pageFooter,
      });
      y = (doc as any).lastAutoTable.finalY + 8;
    });

    // Per-employee totals
    checkNewPage(20);
    doc.setFillColor(...teal);
    doc.rect(margin, y, 2, 7, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...navy);
    doc.text("Personnel Viewing Summary", margin + 5, y + 5);
    y += 10;

    const summaryData = empList.map(([empId, empName]) => {
      const empTimes = reportReadingTimes.filter((rt: any) => rt.user_id === empId);
      const totalSeconds = empTimes.reduce((sum: number, rt: any) => sum + (rt.total_seconds || 0), 0);
      const uniqueDocs = new Set(empTimes.map((rt: any) => rt.document_id)).size;
      return [empName, String(uniqueDocs), String(operatingDocs.length), formatReadingTime(totalSeconds)];
    });

    autoTable(doc, {
      startY: y,
      head: [["Employee", "Documents Viewed", "Total Documents", "Total Viewing Time"]],
      body: summaryData,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2.5, textColor: darkGray, lineColor: [220, 220, 220] as any, lineWidth: 0.2 },
      headStyles: { fillColor: navy, textColor: white, fontStyle: "bold", fontSize: 8.5 },
      alternateRowStyles: { fillColor: [249, 250, 251] as any },
      columnStyles: { 2: { halign: "center" as const }, 3: { halign: "center" as const, fontStyle: "bold" as const } },
      didDrawPage: pageFooter,
    });
    y = (doc as any).lastAutoTable.finalY + 10;

    // Footer band
    checkNewPage(15);
    doc.setFillColor(...navy);
    doc.rect(margin, y, contentW, 10, "F");
    doc.setFillColor(...teal);
    doc.rect(margin, y, contentW, 2, "F");
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...white);
    doc.text("— END OF REPORT —", pageW / 2, y + 7, { align: "center" });

    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(...darkGray);
      doc.text(`Page ${i} of ${totalPages}`, pageW / 2, pageH - 8, { align: "center" });
    }

    doc.save(`Company-Document-View-Report-${now.toISOString().split("T")[0]}.pdf`);
    toast.success("Company Document View Report downloaded");
  };

  // ===================== OCIMF READINESS REPORT =====================
  const generateOCIMFReport = async () => {
    const jsPDFModule = await import("jspdf");
    const jsPDF = jsPDFModule.default;
    const autoTableModule = await import("jspdf-autotable");
    const autoTable = autoTableModule.default;

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentW = pageW - margin * 2;
    let y = 0;

    const now = new Date();
    const dateStr = now.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    const timeStr = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    const refCode = `OCIMF-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    const navy: [number, number, number] = [15, 40, 72];
    const teal: [number, number, number] = [48, 152, 208];
    const white: [number, number, number] = [255, 255, 255];
    const darkGray: [number, number, number] = [55, 65, 81];
    const lightGray: [number, number, number] = [243, 244, 246];

    const pageFooter = () => {
      doc.setFontSize(8);
      doc.setTextColor(...darkGray);
      doc.text(`Velocity Offshore — Confidential`, margin, pageH - 10);
      doc.text(refCode, pageW - margin, pageH - 10, { align: "right" });
    };

    const checkNewPage = (needed: number) => {
      if (y + needed > pageH - 25) {
        pageFooter();
        doc.addPage();
        y = 20;
      }
    };

    // Cover
    doc.setFillColor(...navy);
    doc.rect(0, 0, pageW, 100, "F");
    doc.setFillColor(...teal);
    doc.rect(0, 100, pageW, 3, "F");

    try {
      const logoUrl = new URL("/src/assets/velocity-logo-light.png", window.location.origin).href;
      const logoResponse = await fetch(logoUrl);
      const logoBlob = await logoResponse.blob();
      const logoBase64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(logoBlob);
      });
      doc.addImage(logoBase64, "PNG", (pageW - 70) / 2, 10, 70, 28);
    } catch {}

    doc.setFont("helvetica", "bold");
    doc.setFontSize(28);
    doc.setTextColor(...white);
    doc.text("VELOCITY OFFSHORE", pageW / 2, 50, { align: "center" });
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("Safe. Fast. Cost-efficient.", pageW / 2, 60, { align: "center" });
    doc.setDrawColor(...teal);
    doc.setLineWidth(0.5);
    doc.line(pageW / 2 - 30, 65, pageW / 2 + 30, 65);
    doc.setFontSize(14);
    doc.text("OCIMF READINESS", pageW / 2, 78, { align: "center" });
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("ASSESSMENT REPORT", pageW / 2, 90, { align: "center" });

    y = 115;
    doc.setFontSize(11);
    doc.setTextColor(...darkGray);
    const metaData = [
      ["Report Reference", refCode],
      ["Date Generated", `${dateStr} at ${timeStr}`],
      ["Generated By", fullName || user?.email || "System"],
      ["Classification", "CONFIDENTIAL — Internal Use Only"],
      ["Standards", "OVMSA2 (2nd Ed. 2019) · OVIQ4 v7300 (April 2025)"],
    ];
    metaData.forEach(([label, val]) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(val || "", margin + 50, y);
      y += 8;
    });

    y += 5;
    doc.setFillColor(...lightGray);
    doc.roundedRect(margin, y, contentW, 30, 3, 3, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    const descText = "This report provides a comprehensive overview of Velocity Offshore's readiness against OCIMF's Offshore Vessel Management and Self Assessment (OVMSA2) Second Edition and the Offshore Vessel Inspection Questionnaire (OVIQ4). It summarises element-level and chapter-level compliance scores for management systems and vessel inspections.";
    const descLines = doc.splitTextToSize(descText, contentW - 10);
    doc.text(descLines, margin + 5, y + 8);

    // Build status maps
    const ocimfKpiStatus: Record<string, string> = {};
    const ocimfItemStatus: Record<string, string> = {};
    const ocimfNotes: Record<string, string> = {};
    ocimfItems.forEach((i: any) => {
      if (i.section === "ovmsa") ocimfKpiStatus[i.item_id] = i.status;
      else ocimfItemStatus[i.item_id] = i.status;
      if (i.note) ocimfNotes[i.item_id] = i.note;
    });

    let ovmsaTotal = 0, ovmsaDone = 0;
    OVMSA_ELEMENTS.forEach(e => e.subelements.forEach(s => s.kpis.forEach(k => {
      ovmsaTotal++;
      if (ocimfKpiStatus[k.id] === "complete") ovmsaDone++;
    })));
    const ovmsaPct = ovmsaTotal ? Math.round((ovmsaDone / ovmsaTotal) * 100) : 0;

    let oviqTotal = 0, oviqDone = 0;
    OVIQ_CHAPTERS.forEach(c => c.items.forEach(item => {
      oviqTotal++;
      if (ocimfItemStatus[item.id] === "complete") oviqDone++;
    }));
    const oviqPct = oviqTotal ? Math.round((oviqDone / oviqTotal) * 100) : 0;
    const overallPct = Math.round((ovmsaPct + oviqPct) / 2);

    // Page 2: Scores
    doc.addPage();
    y = 20;

    const sectionHeader = (num: string, title: string) => {
      checkNewPage(20);
      doc.setFillColor(...navy);
      doc.rect(margin, y, contentW, 10, "F");
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...white);
      doc.text(`${num}. ${title}`, margin + 4, y + 7);
      y += 16;
    };

    const subHeader = (title: string) => {
      checkNewPage(12);
      doc.setFillColor(...teal);
      doc.rect(margin, y, 2, 8, "F");
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...navy);
      doc.text(title, margin + 6, y + 6);
      y += 12;
    };

    sectionHeader("1", "OVERALL READINESS SUMMARY");

    doc.setFillColor(...lightGray);
    doc.roundedRect(margin, y, contentW, 28, 2, 2, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...navy);
    doc.text(`Overall OCIMF Readiness: ${overallPct}%`, margin + 5, y + 7);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...darkGray);
    doc.text(`OVMSA2 Score: ${ovmsaPct}% (${ovmsaDone}/${ovmsaTotal} complete)  |  OVIQ4 Score: ${oviqPct}% (${oviqDone}/${oviqTotal} complete)`, margin + 5, y + 15);
    const readinessLabel = overallPct >= 80 ? "READY" : overallPct >= 50 ? "IN PROGRESS" : "ACTION REQUIRED";
    doc.text(`Status: ${readinessLabel}`, margin + 5, y + 23);
    y += 34;

    // OVMSA breakdown
    sectionHeader("2", "OVMSA2 ELEMENT SCORES");
    subHeader("Offshore Vessel Management and Self Assessment — Second Edition");

    const ovmsaTableData = OVMSA_ELEMENTS.map(el => {
      let elTotal = 0, elDone = 0, elPartial = 0;
      el.subelements.forEach(s => s.kpis.forEach(k => {
        elTotal++;
        if (ocimfKpiStatus[k.id] === "complete") elDone++;
        else if (ocimfKpiStatus[k.id] === "partial") elPartial++;
      }));
      const elPct = elTotal ? Math.round((elDone / elTotal) * 100) : 0;
      return [`E${el.code}`, el.title, String(elDone), String(elPartial), String(elTotal - elDone - elPartial), `${elPct}%`];
    });

    autoTable(doc, {
      startY: y,
      head: [["Element", "Title", "Complete", "In Progress", "Not Started", "Score"]],
      body: ovmsaTableData,
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: 2, textColor: darkGray, lineColor: [220, 220, 220] as any, lineWidth: 0.2 },
      headStyles: { fillColor: navy, textColor: white, fontStyle: "bold", fontSize: 8 },
      alternateRowStyles: { fillColor: [249, 250, 251] as any },
      columnStyles: { 0: { cellWidth: 15 }, 5: { halign: "center" as const, fontStyle: "bold" as const } },
      didDrawPage: pageFooter,
    });
    y = (doc as any).lastAutoTable.finalY + 8;

    // OVIQ breakdown
    checkNewPage(20);
    sectionHeader("3", "OVIQ4 CHAPTER SCORES");
    subHeader("Offshore Vessel Inspection Questionnaire — v7300 April 2025");

    const oviqTableData = OVIQ_CHAPTERS.map(ch => {
      const chTotal = ch.items.length;
      const chDone = ch.items.filter(i => ocimfItemStatus[i.id] === "complete").length;
      const chPartial = ch.items.filter(i => ocimfItemStatus[i.id] === "partial").length;
      const chPct = chTotal ? Math.round((chDone / chTotal) * 100) : 0;
      return [ch.code, ch.title, String(chDone), String(chPartial), String(chTotal - chDone - chPartial), `${chPct}%`];
    });

    autoTable(doc, {
      startY: y,
      head: [["Chapter", "Title", "Complete", "In Progress", "Not Started", "Score"]],
      body: oviqTableData,
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: 2, textColor: darkGray, lineColor: [220, 220, 220] as any, lineWidth: 0.2 },
      headStyles: { fillColor: navy, textColor: white, fontStyle: "bold", fontSize: 8 },
      alternateRowStyles: { fillColor: [249, 250, 251] as any },
      columnStyles: { 0: { cellWidth: 15 }, 5: { halign: "center" as const, fontStyle: "bold" as const } },
      didDrawPage: pageFooter,
    });
    y = (doc as any).lastAutoTable.finalY + 8;

    // Document Authenticity
    checkNewPage(40);
    sectionHeader("4", "DOCUMENT AUTHENTICITY");
    doc.setFontSize(9.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    const authText = "This report has been auto-generated by the Velocity Offshore Document Management System. All timestamps are recorded in UTC. Data integrity is maintained through authenticated user sessions with role-based access control.";
    const authLines = doc.splitTextToSize(authText, contentW - 5);
    doc.text(authLines, margin + 2, y);
    y += authLines.length * 4.5 + 8;

    // End band
    checkNewPage(15);
    doc.setFillColor(...navy);
    doc.rect(margin, y, contentW, 12, "F");
    doc.setFillColor(...teal);
    doc.rect(margin, y, contentW, 2, "F");
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...white);
    doc.text("— END OF REPORT —", pageW / 2, y + 8, { align: "center" });

    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(...darkGray);
      doc.text(`Page ${i} of ${totalPages}`, pageW / 2, pageH - 10, { align: "center" });
    }

    doc.save(`OCIMF-Readiness-Report-${now.toISOString().split("T")[0]}.pdf`);
    toast.success("OCIMF Readiness Report downloaded");
  };


  const OpDocCard = ({ doc, showCategory = false }: { doc: any; showCategory?: boolean }) => {
    const isRead = hasRead(doc.id);
    const readTime = getReadingTime(doc.id);
    const docSource = doc.is_sms_review ? "sms_review" : "documents";
    const isSecurityDoc = (doc.category || "").toLowerCase() === "security";

    const isClassFlag = (doc.category || "").toLowerCase() === "class and flag";

    const onCardClick = () => {
      if (isSecurityDoc) {
        handleSecurityDocClick(doc);
      } else if (isClassFlag) {
        handleView(doc.file_path, doc.name, "operating-documents", doc.id, docSource);
      } else {
        handleView(doc.file_path, doc.name, "operating-documents", doc.id, docSource);
      }
    };

    return (
      <Card
        className={`group cursor-pointer hover:shadow-md transition-all ${isSecurityDoc ? "hover:border-destructive/20 border-destructive/10" : "hover:border-teal/20"}`}
        onClick={onCardClick}
      >
        <CardContent className="p-4 flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isSecurityDoc ? "bg-destructive/10" : isClassFlag ? "bg-accent/10" : "bg-teal/10"}`}>
            {isSecurityDoc ? <Shield className="w-5 h-5 text-destructive" /> : isClassFlag ? <ExternalLink className="w-5 h-5 text-accent" /> : <FileText className="w-5 h-5 text-teal" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm text-foreground font-body font-medium truncate transition-colors ${isSecurityDoc ? "group-hover:text-destructive" : "group-hover:text-teal"}`}>{doc.name}</p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <p className="text-xs text-muted-foreground font-body">{new Date(doc.created_at).toLocaleDateString()}</p>
              {doc.version && <span className="px-1.5 py-0.5 rounded bg-teal/10 text-teal text-[10px] font-bold">v{doc.version}</span>}
              {isSecurityDoc && (
                <Badge variant="outline" className="text-destructive border-destructive/20 text-[10px] px-1.5 py-0">
                  <Shield className="w-2.5 h-2.5 mr-0.5" /> Restricted
                </Badge>
              )}
              {showCategory && doc.category && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
                  <Folder className="w-2.5 h-2.5 mr-1" />{doc.category}
                </Badge>
              )}
              {isRead && (
                <Badge variant="outline" className="text-emerald-500 border-emerald-500/20 text-[10px] px-1.5 py-0">
                  <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Read
                </Badge>
              )}
              {readTime > 0 && (
                <Badge variant="outline" className="text-accent border-accent/20 text-[10px] px-1.5 py-0">
                  <Timer className="w-2.5 h-2.5 mr-0.5" /> {formatReadingTime(readTime)}
                </Badge>
              )}
            </div>
            {doc.description && <p className="text-xs text-muted-foreground/70 font-body truncate mt-0.5">{doc.description}</p>}
          </div>
          <div className="flex items-center gap-1">
            {!isRead && !isSecurityDoc && (
                <Button variant="outline" size="sm" className="text-xs" onClick={(e) => { e.stopPropagation(); confirmRead.mutate(doc.id); }}>
                <CheckCircle2 className="w-3 h-3 mr-1" /> Confirm
              </Button>
            )}
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isSecurityDoc) {
                    handleSecurityDocClick(doc);
                  } else {
                    handleView(doc.file_path, doc.name, "operating-documents", doc.id, docSource);
                  }
                }}
                title="Open in viewer"
              >
                {isSecurityDoc ? <Shield className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                {isSecurityDoc ? "Verify & Open" : "Open"}
              </Button>
              {!isSecurityDoc && (
                <Button variant="ghost" size="sm" className="text-teal" onClick={(e) => { e.stopPropagation(); handleDownload(doc.file_path, doc.name, "operating-documents"); }} title="Download">
                  <Download className="w-4 h-4" />
                </Button>
              )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // SMS Review Doc Card — clicking opens the detail panel
  const SmsDocListItem = ({ doc }: { doc: any }) => {
    const isRead = hasRead(doc.id);
    const docSuggestions = getDocSuggestions(doc.id);
    const readTime = getReadingTime(doc.id);

    return (
      <button
        onClick={() => {
          setSmsSelectedDoc(doc);
          setSelectedDocForSuggestion(doc);
          setSuggestionForm({ section_reference: "", change_description: "", justification: "" });
        }}
        className="w-full text-left"
      >
        <Card className="group hover:shadow-md transition-all hover:border-teal/20 cursor-pointer">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
              <ClipboardCheck className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground font-body font-medium truncate group-hover:text-teal transition-colors">{doc.name}</p>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <p className="text-xs text-muted-foreground font-body">{new Date(doc.created_at).toLocaleDateString()}</p>
                {doc.version && <span className="px-1.5 py-0.5 rounded bg-teal/10 text-teal text-[10px] font-bold">v{doc.version}</span>}
                {isRead && (
                  <Badge variant="outline" className="text-emerald-500 border-emerald-500/20 text-[10px] px-1.5 py-0">
                    <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Read
                  </Badge>
                )}
                {readTime > 0 && (
                  <Badge variant="outline" className="text-accent border-accent/20 text-[10px] px-1.5 py-0">
                    <Timer className="w-2.5 h-2.5 mr-0.5" /> {formatReadingTime(readTime)}
                  </Badge>
                )}
                {docSuggestions.length > 0 && (
                  <Badge variant="outline" className="text-amber-500 border-amber-500/20 text-[10px] px-1.5 py-0">
                    <MessageSquarePlus className="w-2.5 h-2.5 mr-0.5" /> {docSuggestions.length} suggestion{docSuggestions.length !== 1 ? "s" : ""}
                  </Badge>
                )}
              </div>
              {doc.description && <p className="text-xs text-muted-foreground/70 font-body truncate mt-0.5">{doc.description}</p>}
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-teal transition-colors flex-shrink-0" />
          </CardContent>
        </Card>
      </button>
    );
  };

  // ===================== FOLDER GRIDS =====================

  const FolderGrid = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {DOCUMENT_FOLDERS.map((folder) => {
        const count = folderCounts[folder.key] || 0;
        const isRestricted = (folder as any).restricted;
        const hasAccess = !isRestricted || hasSecurityAccess;
        return (
          <button key={folder.key} onClick={() => {
            if (isRestricted && !hasAccess) {
              toast.error("You do not have security clearance. Contact your administrator.");
              return;
            }
            setActiveFolder(folder.key); setSearch("");
          }}
            className={`group text-left p-5 rounded-2xl border bg-card hover:shadow-lg transition-all duration-200 ${isRestricted ? "border-destructive/20 hover:border-destructive/40" : "border-border hover:border-teal/30"}`}>
            <div className="flex items-start justify-between mb-3">
              <span className="text-2xl">{folder.icon}</span>
              <div className="flex items-center gap-1">
                {isRestricted && (
                  <Badge variant="outline" className="text-[10px] text-destructive border-destructive/20">Restricted</Badge>
                )}
                <Badge variant="outline" className="text-[10px] text-muted-foreground">{count} {count === 1 ? "doc" : "docs"}</Badge>
              </div>
            </div>
            <h3 className={`font-display text-sm font-semibold transition-colors ${isRestricted ? "text-foreground group-hover:text-destructive" : "text-foreground group-hover:text-teal"}`}>{folder.label}</h3>
            <p className="text-[11px] text-muted-foreground font-body mt-1 line-clamp-2">{folder.description}</p>
          </button>
        );
      })}
    </div>
  );

  const SmsFolderGrid = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {SMS_REVIEW_FOLDERS.map((folder) => {
        const count = smsFolderCounts[folder.key] || 0;
        const isRestricted = (folder as any).restricted;
        const isLocked = isRestricted && (!hasSecurityAccess || !securityCleared);
        return (
          <button key={folder.key}
            onClick={() => {
              if (isLocked) {
                if (!hasSecurityAccess) {
                  toast.error("Security clearance required. Contact your administrator.");
                  return;
                }
                setShowSecurityAuth(true);
                return;
              }
              setSmsActiveFolder(folder.key); setSearch(""); setSmsSelectedDoc(null);
            }}
            className={`group text-left p-5 rounded-2xl border bg-card transition-all duration-200 ${isLocked ? "border-destructive/20 hover:border-destructive/40 opacity-75" : "border-border hover:border-amber-500/30 hover:shadow-lg"}`}>
            <div className="flex items-start justify-between mb-3">
              <span className="text-2xl">{folder.icon}</span>
              <div className="flex items-center gap-1.5">
                {isLocked && <Shield className="w-3.5 h-3.5 text-destructive" />}
                <Badge variant="outline" className="text-[10px] text-muted-foreground">{isLocked ? "Restricted" : `${count} ${count === 1 ? "doc" : "docs"}`}</Badge>
              </div>
            </div>
            <h3 className={`font-display text-sm font-semibold transition-colors ${isLocked ? "text-foreground group-hover:text-destructive" : "text-foreground group-hover:text-amber-600"}`}>{folder.label}</h3>
            <p className="text-[11px] text-muted-foreground font-body mt-1 line-clamp-2">{folder.description}</p>
          </button>
        );
      })}
    </div>
  );

  // ===================== FOLDER DETAIL VIEWS =====================

  const FolderDetail = () => {
    const folder = DOCUMENT_FOLDERS.find((f) => f.key === activeFolder);
    if (!folder) return null;
    return (
      <div>
        <div className="flex items-center gap-2 mb-5">
          <button onClick={() => { setActiveFolder(null); setSearch(""); }}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-teal transition-colors font-body">
            <ArrowLeft className="w-4 h-4" /> All Folders
          </button>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-sm text-foreground font-body font-medium flex items-center gap-1.5">
            <span>{folder.icon}</span> {folder.label}
          </span>
          <Badge variant="outline" className="text-[10px] ml-1 text-muted-foreground">{folderDocs.length} documents</Badge>
        </div>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-body"><SlidersHorizontal className="w-3.5 h-3.5" /> Sort by:</div>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
            <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="name">Name</SelectItem><SelectItem value="date">Date Added</SelectItem></SelectContent>
          </Select>
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-muted-foreground" onClick={() => setSortOrder((o) => o === "asc" ? "desc" : "asc")}>
            {sortOrder === "asc" ? "A→Z" : "Z→A"}
          </Button>
        </div>
        {folderDocs.length === 0 ? (
          <Card><CardContent className="p-12 text-center">
            <Folder className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
            <p className="text-muted-foreground font-body text-sm">{search ? "No documents match your search." : "No documents in this folder yet."}</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-2">{folderDocs.map((doc) => <OpDocCard key={doc.id} doc={doc} />)}</div>
        )}
      </div>
    );
  };

  const SmsFolderDetail = () => {
    const folder = SMS_REVIEW_FOLDERS.find((f) => f.key === smsActiveFolder);
    if (!folder) return null;

    // If a doc is selected, show the detail panel
    if (smsSelectedDoc) return <SmsDocDetailPanel />;

    return (
      <div>
        <div className="flex items-center gap-2 mb-5">
          <button onClick={() => { setSmsActiveFolder(null); setSearch(""); setSmsSelectedDoc(null); }}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-amber-600 transition-colors font-body">
            <ArrowLeft className="w-4 h-4" /> All Folders
          </button>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-sm text-foreground font-body font-medium flex items-center gap-1.5">
            <span>{folder.icon}</span> {folder.label}
          </span>
          <Badge variant="outline" className="text-[10px] ml-1 text-muted-foreground">{smsFolderDocs.length} documents</Badge>
        </div>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-body"><SlidersHorizontal className="w-3.5 h-3.5" /> Sort by:</div>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
            <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="name">Name</SelectItem><SelectItem value="date">Date Added</SelectItem></SelectContent>
          </Select>
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-muted-foreground" onClick={() => setSortOrder((o) => o === "asc" ? "desc" : "asc")}>
            {sortOrder === "asc" ? "A→Z" : "Z→A"}
          </Button>
        </div>
        {smsFolderDocs.length === 0 ? (
          <Card><CardContent className="p-12 text-center">
            <Folder className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
            <p className="text-muted-foreground font-body text-sm">{search ? "No documents match your search." : "No documents in this folder yet."}</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-2">{smsFolderDocs.map((doc) => <SmsDocListItem key={doc.id} doc={doc} />)}</div>
        )}
      </div>
    );
  };

  // ===================== SMS DOC DETAIL PANEL =====================
  const SmsDocDetailPanel = () => {
    const doc = smsSelectedDoc;
    if (!doc) return null;

    const docSuggestions = getDocSuggestions(doc.id);
    const isRead = hasRead(doc.id);
    const folder = SMS_REVIEW_FOLDERS.find((f) => f.key === smsActiveFolder);

    return (
      <div>
        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <button onClick={() => { setSmsActiveFolder(null); setSearch(""); setSmsSelectedDoc(null); }}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-amber-600 transition-colors font-body">
            <ArrowLeft className="w-4 h-4" /> All Folders
          </button>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          <button onClick={() => setSmsSelectedDoc(null)}
            className="text-sm text-muted-foreground hover:text-amber-600 transition-colors font-body flex items-center gap-1">
            {folder?.icon} {folder?.label}
          </button>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-sm text-foreground font-body font-medium truncate max-w-xs">{doc.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT: Document info + Propose Change Form */}
          <div className="space-y-5">
            {/* Document Info Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                    <ClipboardCheck className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="font-display text-base leading-tight">{doc.name}</CardTitle>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {doc.version && <span className="px-1.5 py-0.5 rounded bg-teal/10 text-teal text-[10px] font-bold">v{doc.version}</span>}
                      <span className="text-xs text-muted-foreground">{new Date(doc.created_at).toLocaleDateString()}</span>
                      {isRead ? (
                        <Badge variant="outline" className="text-emerald-500 border-emerald-500/20 text-[10px]"><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Read</Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-500 border-amber-500/20 text-[10px]">Unread</Badge>
                      )}
                      {getReadingTime(doc.id) > 0 && (
                        <Badge variant="outline" className="text-accent border-accent/20 text-[10px]">
                          <Timer className="w-2.5 h-2.5 mr-0.5" /> {formatReadingTime(getReadingTime(doc.id))}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {doc.description && <p className="text-xs text-muted-foreground font-body">{doc.description}</p>}
                <div className="flex items-center gap-2">
                  {!isRead && (
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => confirmRead.mutate(doc.id)}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Confirm Read
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => handleView(doc.file_path, doc.name, "operating-documents", doc.id, "sms_review")}>
                    <Eye className="w-3 h-3 mr-1" /> Open in Viewer
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => handleDownload(doc.file_path, doc.name, "operating-documents")}>
                    <Download className="w-3 h-3 mr-1" /> Download
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Propose Change Form */}
            <Card className="border-amber-500/20">
              <CardHeader className="pb-3">
                <CardTitle className="font-display text-sm flex items-center gap-2">
                  <MessageSquarePlus className="w-4 h-4 text-amber-600" />
                  Propose a Change
                </CardTitle>
                <p className="text-xs text-muted-foreground font-body">
                  Submit a proposed amendment to this document per ISM Code Ch.12 and ISO 9001 Cl.10.3.
                  Your submission will be timestamped and reviewed by the Executive Team.
                </p>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                <div>
                  <label className="text-xs font-body text-muted-foreground mb-1 block">Section Reference (optional)</label>
                  <Input
                    value={suggestionForm.section_reference}
                    onChange={(e) => setSuggestionForm((p) => ({ ...p, section_reference: e.target.value }))}
                    placeholder="e.g. Section 4.2.1, Chapter 7, Appendix B"
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-body text-muted-foreground mb-1 block">Describe the Proposed Change *</label>
                  <Textarea
                    value={suggestionForm.change_description}
                    onChange={(e) => setSuggestionForm((p) => ({ ...p, change_description: e.target.value }))}
                    placeholder="Clearly describe what should be changed, added, or removed from this document..."
                    rows={4}
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-body text-muted-foreground mb-1 block">Justification (optional)</label>
                  <Textarea
                    value={suggestionForm.justification}
                    onChange={(e) => setSuggestionForm((p) => ({ ...p, justification: e.target.value }))}
                    placeholder="Why is this change needed? Reference any regulatory requirements, incidents, or operational improvements..."
                    rows={3}
                    className="text-sm"
                  />
                </div>
                <Button
                  onClick={() => submitSuggestion.mutate()}
                  disabled={!suggestionForm.change_description.trim() || submitSuggestion.isPending}
                  className="w-full"
                >
                  {submitSuggestion.isPending ? "Submitting..." : "Submit Change Proposal"}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT: Previous Comments / Suggestions History */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <History className="w-4 h-4 text-muted-foreground" />
              <h3 className="font-display text-sm text-foreground">Review History</h3>
              <Badge variant="outline" className="text-[10px] text-muted-foreground">{docSuggestions.length} proposal{docSuggestions.length !== 1 ? "s" : ""}</Badge>
            </div>

            {docSuggestions.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <MessageSquarePlus className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground font-body">No change proposals yet.</p>
                  <p className="text-xs text-muted-foreground/60 font-body mt-1">Be the first to suggest improvements to this document.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
                {docSuggestions.map((s: any, idx: number) => {
                  const sVotes = getSuggestionVotes(s.id);
                  const approvedVotes = sVotes.filter((v: any) => v.vote === "approved");
                  const rejectedVotes = sVotes.filter((v: any) => v.vote === "rejected");
                  const alreadyVoted = hasVoted(s.id);
                  const submitterName = getProfileName(s.suggested_by);

                  return (
                    <Card key={s.id} className="border-l-4 border-l-amber-500/40">
                      <CardContent className="p-4 space-y-3">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <User className="w-3.5 h-3.5 text-muted-foreground" />
                              <span className="text-xs font-body font-medium text-foreground">{submitterName}</span>
                              {getStatusBadge(s.status)}
                            </div>
                            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              {new Date(s.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>

                        {/* Section */}
                        {s.section_reference && (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <BookOpen className="w-3 h-3" />
                            <span>Section: <strong className="text-foreground">{s.section_reference}</strong></span>
                          </div>
                        )}

                        {/* Change Description */}
                        <div className="bg-muted/50 rounded-lg p-3">
                          <p className="text-xs font-body text-foreground whitespace-pre-wrap">{s.change_description}</p>
                        </div>

                        {/* Justification */}
                        {s.justification && (
                          <div className="bg-muted/30 rounded-lg p-3">
                            <p className="text-[10px] text-muted-foreground font-body font-bold uppercase tracking-wider mb-1">Justification</p>
                            <p className="text-xs font-body text-muted-foreground italic whitespace-pre-wrap">{s.justification}</p>
                          </div>
                        )}

                        {/* Votes */}
                        <div className="flex items-center gap-4 text-[10px]">
                          <span className="text-emerald-500 flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> {approvedVotes.length}</span>
                          <span className="text-destructive flex items-center gap-1"><ThumbsDown className="w-3 h-3" /> {rejectedVotes.length}</span>
                        </div>

                        {/* Voter Comments */}
                        {sVotes.length > 0 && (
                          <div className="space-y-1.5 pl-3 border-l-2 border-border">
                            {sVotes.map((v: any) => (
                              <div key={v.id} className="text-[10px]">
                                <span className={v.vote === "approved" ? "text-emerald-500" : "text-destructive"}>
                                  {v.vote === "approved" ? "✓" : "✗"} {v.voter_name || "Unknown"}
                                </span>
                                {v.comments && <span className="text-muted-foreground ml-1">— "{v.comments}"</span>}
                                <span className="text-muted-foreground/50 ml-1">{new Date(v.created_at).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Vote Button */}
                        {canVote && !alreadyVoted && s.status === "pending" && (
                          <Button variant="outline" size="sm" className="text-xs w-full" onClick={() => { setSelectedSuggestion(s); setShowVoteDialog(true); }}>
                            <Shield className="w-3 h-3 mr-1" /> Cast Your Vote
                          </Button>
                        )}
                        {canVote && alreadyVoted && (
                          <Badge variant="outline" className="text-[10px] text-muted-foreground">You have voted on this proposal</Badge>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ===================== SEARCH RESULTS =====================

  const SearchResults = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-teal" />
          <h3 className="text-sm font-body font-medium text-foreground">{globalSearchResults.length} result{globalSearchResults.length !== 1 ? "s" : ""} for "{search}"</h3>
        </div>
        <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => setSearch("")}><X className="w-3 h-3 mr-1" /> Clear</Button>
      </div>
      {globalSearchResults.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Search className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-muted-foreground font-body text-sm">No documents found.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">{globalSearchResults.map((doc) => <OpDocCard key={doc.id} doc={doc} showCategory />)}</div>
      )}
    </div>
  );

  const SmsSearchResults = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-amber-600" />
          <h3 className="text-sm font-body font-medium text-foreground">{smsGlobalSearchResults.length} result{smsGlobalSearchResults.length !== 1 ? "s" : ""} for "{search}"</h3>
        </div>
        <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => setSearch("")}><X className="w-3 h-3 mr-1" /> Clear</Button>
      </div>
      {smsGlobalSearchResults.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Search className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-muted-foreground font-body text-sm">No SMS review documents found.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">{smsGlobalSearchResults.map((doc) => <SmsDocListItem key={doc.id} doc={doc} />)}</div>
      )}
    </div>
  );

  // ===================== RENDER =====================

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-foreground">Documents & SOPs</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">Company operating documents, SMS reviews, and client documents</p>
        </div>
        {canGenerateReport && (
          <Button variant="outline" onClick={() => setShowReportDialog(true)} className="gap-2" disabled={reportReadingTimesLoading}>
            <FileBarChart className="w-4 h-4" />
            {reportReadingTimesLoading ? "Loading Reading Data..." : "Generate Compliance Report"}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-border">
        <button onClick={() => { setActiveTab("operating"); setActiveFolder(null); setSmsActiveFolder(null); setSmsSelectedDoc(null); setSearch(""); }} className={`flex items-center gap-2 px-4 py-3 text-sm font-body font-medium border-b-2 transition-all ${activeTab === "operating" ? "border-teal text-teal" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <FolderOpen className="w-4 h-4" />Company Documents
        </button>
        <button onClick={() => { setActiveTab("sms_reviews"); setActiveFolder(null); setSmsActiveFolder(null); setSmsSelectedDoc(null); setSearch(""); }} className={`flex items-center gap-2 px-4 py-3 text-sm font-body font-medium border-b-2 transition-all ${activeTab === "sms_reviews" ? "border-teal text-teal" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <ClipboardCheck className="w-4 h-4" />SMS Reviews
          {smsReviewDocs.length > 0 && <span className="px-1.5 py-0.5 rounded-full bg-teal/10 text-teal text-[10px] font-bold">{smsReviewDocs.length}</span>}
        </button>
        <button onClick={() => { setActiveTab("client"); setActiveFolder(null); setSmsActiveFolder(null); setSmsSelectedDoc(null); setSearch(""); }} className={`flex items-center gap-2 px-4 py-3 text-sm font-body font-medium border-b-2 transition-all ${activeTab === "client" ? "border-teal text-teal" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <FileText className="w-4 h-4" />Client Documents
        </button>
        <button onClick={() => { setActiveTab("ocimf"); setActiveFolder(null); setSmsActiveFolder(null); setSmsSelectedDoc(null); setSearch(""); }} className={`flex items-center gap-2 px-4 py-3 text-sm font-body font-medium border-b-2 transition-all ${activeTab === "ocimf" ? "border-teal text-teal" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <Shield className="w-4 h-4" />OCIMF Readiness
        </button>
      </div>

      {/* Search bar */}
      <div className="flex gap-3 mb-6 flex-col sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              activeTab === "operating"
                ? activeFolder ? `Search in ${activeFolder}...` : "Search all documents by name, keyword, category..."
                : activeTab === "sms_reviews"
                  ? smsActiveFolder ? `Search in ${smsActiveFolder}...` : "Search SMS review documents..."
                  : "Search documents..."
            }
            className="pl-10"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors" />
            </button>
          )}
        </div>
        {activeTab === "client" && (
          <Select value={filterCompany || "all"} onValueChange={(v) => setFilterCompany(v === "all" ? "" : v)}>
            <SelectTrigger className="min-w-[180px]"><SelectValue placeholder="All companies" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All companies</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* ========== COMPANY DOCUMENTS TAB (Folder-based) ========== */}
      {activeTab === "operating" && (
        opLoading ? (
          <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
        ) : search && !activeFolder ? (
          <SearchResults />
        ) : activeFolder ? (
          <FolderDetail />
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-6 text-xs text-muted-foreground font-body">
              <span className="flex items-center gap-1.5"><Folder className="w-3.5 h-3.5 text-teal" /> {DOCUMENT_FOLDERS.length} folders</span>
              <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-teal" /> {nonSmsOpDocs.length} documents</span>
            </div>
            <FolderGrid />
          </div>
        )
      )}

      {/* ========== SMS REVIEWS TAB (Folder-based with click-to-propose) ========== */}
      {activeTab === "sms_reviews" && (
        opLoading ? (
          <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
        ) : smsSelectedDoc && smsActiveFolder ? (
          <SmsDocDetailPanel />
        ) : search && !smsActiveFolder ? (
          <SmsSearchResults />
        ) : smsActiveFolder ? (
          <SmsFolderDetail />
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-6 text-xs text-muted-foreground font-body">
              <span className="flex items-center gap-1.5"><Folder className="w-3.5 h-3.5 text-amber-600" /> {SMS_REVIEW_FOLDERS.length} folders</span>
              <span className="flex items-center gap-1.5"><ClipboardCheck className="w-3.5 h-3.5 text-amber-600" /> {smsReviewDocs.length} review documents</span>
              <span className="flex items-center gap-1.5"><MessageSquarePlus className="w-3.5 h-3.5 text-amber-600" /> {suggestions.length} proposals</span>
            </div>

            {/* ISM / ISO Context Banner */}
            <Card className="border-amber-500/20 bg-amber-500/5">
              <CardContent className="p-4 flex items-start gap-3">
                <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-body font-medium text-foreground">SMS Document Review Process</p>
                  <p className="text-xs text-muted-foreground font-body mt-1">
                    This review process satisfies <strong>ISM Code Chapter 11 & 12</strong> (Documentation & Company Verification)
                    and <strong>ISO 9001:2015 Clause 7.5 & 10.3</strong> (Documented Information & Continual Improvement).
                    Click any document to propose changes or view previous submissions.
                  </p>
                </div>
              </CardContent>
            </Card>

            <SmsFolderGrid />
          </div>
        )
      )}

      {/* ========== CLIENT DOCUMENTS TAB ========== */}
      {activeTab === "client" && (
        isLoading ? (
          <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="p-16 text-center"><FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" /><p className="text-muted-foreground font-body">No documents found.</p></CardContent></Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((doc) => {
              const readTime = getReadingTime(doc.id);
              return (
                <Card key={doc.id} className="group hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleView(doc.file_path, doc.name, "client-documents", doc.id)}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-teal/10 flex items-center justify-center flex-shrink-0"><FileText className="w-5 h-5 text-teal" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground font-body font-medium truncate group-hover:text-teal transition-colors">{doc.name}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <p className="text-xs text-muted-foreground font-body">{(doc.companies as { name: string } | null)?.name ?? "—"} · {new Date(doc.created_at).toLocaleDateString()}</p>
                        {readTime > 0 && (
                          <Badge variant="outline" className="text-accent border-accent/20 text-[10px] px-1.5 py-0">
                            <Timer className="w-2.5 h-2.5 mr-0.5" /> {formatReadingTime(readTime)}
                          </Badge>
                        )}
                      </div>
                      {doc.description && <p className="text-xs text-muted-foreground/70 font-body truncate mt-0.5">{doc.description}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" className="text-xs" onClick={(e) => { e.stopPropagation(); handleView(doc.file_path, doc.name, "client-documents", doc.id, "documents"); }}><Eye className="w-3 h-3 mr-1" />Open</Button>
                      <Button variant="ghost" size="sm" className="text-teal" onClick={(e) => { e.stopPropagation(); handleDownload(doc.file_path, doc.name); }}><Download className="w-4 h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )
      )}

      {/* ========== OCIMF READINESS TAB ========== */}
      {activeTab === "ocimf" && (
        <Suspense fallback={<div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>}>
          <OCIMFReadinessCheck />
        </Suspense>
      )}

      {/* Document Viewer Modal */}
      <Dialog open={!!viewerUrl} onOpenChange={(open) => { if (!open) resetViewer(); }}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1 pr-4">
                <DialogTitle className="font-display text-lg truncate">{viewerName}</DialogTitle>
                <DialogDescription className="mt-1 text-[11px]">
                  This pop-up viewer tracks reading time while the document is open.
                </DialogDescription>
                {viewerDocId && (
                  <p className="text-[10px] text-muted-foreground font-body flex items-center gap-1.5 mt-1">
                    <Timer className="w-3 h-3 text-accent" /> Reading time is being tracked
                    {getReadingTime(viewerDocId) > 0 && (
                      <span className="ml-1 text-accent font-medium">
                        — Total: {formatReadingTime(getReadingTime(viewerDocId))}
                      </span>
                    )}
                  </p>
                )}
              </div>
              <Button variant="ghost" size="sm" className="text-teal flex-shrink-0" onClick={() => { if (viewerUrl) window.open(viewerUrl, "_blank"); }}>
                <ExternalLink className="w-4 h-4 mr-1" />Open in new tab
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 px-6 pb-6 min-h-0">
            {viewerEmbedUrl && <iframe src={viewerEmbedUrl} className="w-full h-full rounded-lg border border-border bg-background" title={viewerName} />}
          </div>
        </DialogContent>
      </Dialog>

      {/* Vote Dialog */}
      <Dialog open={showVoteDialog} onOpenChange={(open) => { if (!open) { setShowVoteDialog(false); setSelectedSuggestion(null); setVoteComment(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Cast Your Vote</DialogTitle>
            <DialogDescription>
              {selectedSuggestion?.document_name} — {selectedSuggestion?.section_reference || "General"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm text-foreground font-body">{selectedSuggestion?.change_description}</p>
            </div>
            <div>
              <label className="text-xs font-body text-muted-foreground mb-1 block">Comments (optional)</label>
              <Textarea value={voteComment} onChange={(e) => setVoteComment(e.target.value)} placeholder="Add comments to your vote..." rows={2} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="text-destructive border-destructive/20 hover:bg-destructive/10" onClick={() => submitVote.mutate("rejected")} disabled={submitVote.isPending}>
              <ThumbsDown className="w-4 h-4 mr-1" /> Reject
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => submitVote.mutate("approved")} disabled={submitVote.isPending}>
              <ThumbsUp className="w-4 h-4 mr-1" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Security Clearance Code Dialog */}
      <Dialog open={showSecurityAuth} onOpenChange={(open) => { if (!open) { setShowSecurityAuth(false); setPendingSecurityDoc(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Shield className="w-5 h-5 text-destructive" /> Security Clearance Required
            </DialogTitle>
            <DialogDescription>
              This folder contains classified security documentation. Enter the unique security access code provided by your administrator.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-body text-muted-foreground mb-1.5 block">Security Access Code</label>
              <Input
                type="text"
                value={securityCodeInput}
                onChange={(e) => setSecurityCodeInput(e.target.value.toUpperCase())}
                placeholder="e.g. SEC-A3K7M2P9"
                className="font-mono tracking-widest text-center text-lg"
                onKeyDown={(e) => { if (e.key === "Enter") handleSecurityCodeVerify(); }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSecurityAuth(false); setPendingSecurityDoc(null); }}>Cancel</Button>
            <Button
              onClick={handleSecurityCodeVerify}
              disabled={securityAuthLoading}
              className="bg-destructive hover:bg-destructive/80"
            >
              {securityAuthLoading ? "Verifying..." : "Verify & Access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Selection Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileBarChart className="w-5 h-5 text-teal" />
              Generate Compliance Report
            </DialogTitle>
            <DialogDescription>
              Choose which report to generate. Each report will download as a branded PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <button
              onClick={() => { setShowReportDialog(false); generateReport(); }}
              className="w-full text-left p-4 rounded-lg border border-border hover:border-teal/40 hover:bg-teal/5 transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-teal/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <ClipboardCheck className="w-4.5 h-4.5 text-teal" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground group-hover:text-teal transition-colors">SMS Review Report</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Document review compliance, read confirmations, change suggestions, and voting records.</p>
                </div>
              </div>
            </button>
            <button
              onClick={() => { setShowReportDialog(false); generateDocumentViewReport(); }}
              className="w-full text-left p-4 rounded-lg border border-border hover:border-teal/40 hover:bg-teal/5 transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Eye className="w-4.5 h-4.5 text-accent" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground group-hover:text-teal transition-colors">Company Document View Report</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Who opened each company document and how long they viewed it.</p>
                </div>
              </div>
            </button>
            <button
              onClick={() => { setShowReportDialog(false); generateOCIMFReport(); }}
              className="w-full text-left p-4 rounded-lg border border-border hover:border-teal/40 hover:bg-teal/5 transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Shield className="w-4.5 h-4.5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground group-hover:text-teal transition-colors">OCIMF Readiness Report</p>
                  <p className="text-xs text-muted-foreground mt-0.5">OVMSA2 management assessment and OVIQ4 vessel inspection readiness scores.</p>
                </div>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const EmployeeDocumentsContent = EmployeeDocuments;

const EmployeeDocumentsPage = () => (
  <IntranetLayout>
    <EmployeeDocumentsContent />
  </IntranetLayout>
);

export default EmployeeDocumentsPage;
